import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getApolloContext } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation, Navigate, Outlet, matchPath } from 'react-router-dom';
import Config from '../../common/Config';
import { returnToOf } from './returnTo';

// Utils
import { resolveEndpoints } from '@repo/front-end-utils';
import createGraphQLApolloClient, {
  ExtendedApolloClient,
} from '../../common/utils/createGraphQLApolloClient';
import { extractClaims } from '@repo/front-end-utils';
import { Roles } from '@repo/frontend-types';

// Context
import { DataContextProvider } from '../../common/contexts/DataContext';
import { DialogContextProvider, useUserStore, EventContextProvider } from '@repo/front-end-state';
import { DialogRenderer } from '@repo/ui';

// Components
import { TLSpinnerPage } from '@repo/ui';

import { getOrganisationById, getLegalStatus } from '@repo/front-end-services';
import { hasLegalPass, rememberLegalPass } from './legalCheckMemory';
import { PlanHandoverLayer } from '../3D/planView/PlanHandoverLayer';
import { OnboardingModalLayer } from '../../pages/Onboarding/OnboardingModalLayer';

/**
 * One protected page: its path and the roles allowed on it. Absent means any signed-in role; an
 * empty array admits no one (the role check sends every user to /permission-denied).
 */
export interface ProtectedRouteEntry {
  path: string;
  allowedRoles?: string[];
}

/**
 * The signed-in person's roles, for deciding whether the terms check applies: from the Auth0 user
 * object (the server-side source of truth, never localStorage), else the user store's roles while
 * the user object has none yet. Empty means not known yet.
 */
const rolesOf = (user: object, authRoles: string[] | undefined): string[] => {
  let roles: string[] = [];
  try {
    const claims = extractClaims(user as any, Config.auth0Namespace);
    roles = claims.roles || [];
  } catch (extractError) {
    if (Config.auth0Namespace) {
      const rolesPath = `${Config.auth0Namespace}/jwt/claims/roles`;
      if ((user as any)[rolesPath]) {
        roles = (user as any)[rolesPath] || [];
      }
    }
  }
  if ((!roles || roles.length === 0) && authRoles && authRoles.length > 0) {
    roles = authRoles;
  }
  if (!Array.isArray(roles)) {
    roles = roles ? [roles] : [];
  }
  return roles;
};

interface GateProps {
  allowedRoles?: string[];
  children: React.ReactNode;
}

/**
 * The sign-in, role and terms gate, and the provider stack every protected page renders inside.
 * Used by `ProtectedLayout` (the layout route above `/twin`, `/onboarding` and the dev fixtures)
 * and by `ProtectedRoute` (one page, for the account pages): one implementation, two entry points.
 *
 * The stack is rendered in one position whatever the Apollo client's state, so the client's arrival
 * never remounts the providers or the page, and it stays mounted across route changes within the
 * layout. What a remount used to clear on a route change is cleared through each provider's
 * `resetKey`.
 */
export const ProtectedGate: React.FC<GateProps> = ({ allowedRoles, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, user, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { authMetadata, setAuthMetadata } = useUserStore();
  // The terms key that last passed in this gate's mount, and the redirect owed for a key.
  const [legalPassedFor, setLegalPassedFor] = useState<string | null>(null);
  const [legalRedirect, setLegalRedirect] = useState<{ key: string; state: any } | null>(null);
  // The page the person is on, for a terms redirect's way back, read when the answer arrives.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  // A signed-out visit is sent to the login page carrying where it was going (`returnTo.ts`).
  // Held in a ref so the auth effect below reads the current page without re-running on every change.
  const loginState = useRef({ returnTo: '' });
  loginState.current = { returnTo: returnToOf(location) };

  // Effect for auth handling - fetch token
  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);
      } catch (e) {
        console.error('Failed to get access token:', e);
        // Only redirect to login if we're sure the user is not authenticated
        // The SDK will handle silent authentication automatically
        if (!isLoading) {
          // Redirect to login if silent auth fails, coming back to this page afterwards.
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search },
          });
        }
      }
    };

    if (isAuthenticated) {
      getAccessToken();
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, isLoading]);

  // The terms check is asked once per person and kind of page, not once per page. Its key is null on
  // the terms page itself and on a page only roles that never owe terms may open (TL_ADMIN,
  // PARTNER_USER); otherwise it names the person, so moving between pages with the same key needs no
  // check. Completeness is worked out here, during render, so a move never renders a spinner frame.
  // An empty `allowedRoles` admits no one, but counts here as admitting customers, as it always has:
  // the role check below sends everyone to /permission-denied anyway.
  const pageAdmitsCustomers =
    !allowedRoles ||
    allowedRoles.length === 0 ||
    allowedRoles.some(role => role === Roles.CUSTOMER_ADMIN || role === Roles.CUSTOMER_USER);
  const customerKey = `${user?.sub ?? ''}|customer`;
  const legalKey =
    location.pathname === '/account/legal' || !pageAdmitsCustomers ? null : customerKey;
  // Not signed in counts as complete: the sign-in redirect below takes over.
  const isLegalCheckComplete =
    !isAuthenticated ||
    !user ||
    legalKey === null ||
    legalPassedFor === legalKey ||
    hasLegalPass(legalKey);
  // A redirect stands only for the key it was raised for, so a change of person clears it.
  const shouldRedirectToLegal = legalRedirect !== null && legalRedirect.key === legalKey;
  const legalRedirectState = shouldRedirectToLegal ? legalRedirect.state : null;
  const authRoles = authMetadata?.roles;

  // Terms and Conditions check effect: runs for a key with no pass, never on a move between pages.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) {
      return;
    }

    // Staff on a page with no terms key (onboarding, reached first by a reload, a link or a new
    // twin): settle their customer-page key now, from their roles alone, so the move into the viewer
    // needs no check and shows no spinner. A customer here is left alone: no status call is made for
    // a page that does not need one.
    if (legalKey === null) {
      if (legalPassedFor === customerKey || hasLegalPass(customerKey)) return;
      const roles = rolesOf(user, authRoles);
      const holdsCustomerRole =
        roles.includes(Roles.CUSTOMER_ADMIN) || roles.includes(Roles.CUSTOMER_USER);
      if (roles.length > 0 && !holdsCustomerRole) {
        setLegalPassedFor(customerKey);
        if (user.sub) {
          rememberLegalPass(customerKey);
        }
      }
      return;
    }

    // Wait for the access token the status call needs. `key` narrows `legalKey` for the closure.
    if (isLegalCheckComplete || !accessToken) {
      return;
    }
    const key: string = legalKey;

    // The legal gate is computed SERVER-SIDE (spec-legal-gate-rework FR4): the frontend holds no
    // legal logic. We still read roles from the Auth0 user object only to decide whether the gate
    // even applies (customer roles) and to avoid an unnecessary status call for staff; the endpoint
    // recomputes authoritatively from the token. Fail-open (NF2): any status failure proceeds as
    // valid — a status check must never block a legitimate login.
    let cancelled = false;
    const checkLegal = async () => {
      try {
        const roles = rolesOf(user, authRoles);

        // No roles yet — wait for user data to be available; never allow bypass.
        if (roles.length === 0) {
          return;
        }

        // Only CUSTOMER_ADMIN / CUSTOMER_USER face the gate; TL_ADMIN / PARTNER_USER never do (the
        // status endpoint also returns [] for them, but we skip the call entirely for staff).
        const hasCustomerRole =
          roles.includes(Roles.CUSTOMER_ADMIN) || roles.includes(Roles.CUSTOMER_USER);

        if (hasCustomerRole) {
          try {
            const status = await getLegalStatus(Config.middlewareApi, accessToken);
            if (cancelled) return;
            if (status.required.length > 0) {
              // Never remembered: the gate after signing asks again.
              setLegalRedirect({
                key,
                state: {
                  required: status.required,
                  organisationBid: status.organisationBid,
                  redirectPath: pathnameRef.current,
                },
              });
              return;
            }
          } catch (error) {
            // Fail-open: proceed as valid, log loudly. Passed for this mount only, so the next
            // fresh gate asks again.
            console.warn('Legal status check failed — proceeding as valid:', error);
            if (!cancelled) {
              setLegalPassedFor(key);
            }
            return;
          }
        }

        // A real pass: nothing owed, or roles that never owe terms. Remembered for the page load
        // only when the person is identifiable.
        if (!cancelled) {
          setLegalPassedFor(key);
          if (user.sub) {
            rememberLegalPass(key);
          }
        }
      } catch (error) {
        // Fail-open (NF2): any unexpected error still lets the user into the app rather than
        // hanging on the spinner (which gates on isLegalCheckComplete). This mount only.
        console.error('Error checking Terms and Conditions:', error);
        if (!cancelled) {
          setLegalPassedFor(key);
        }
      }
    };

    checkLegal();
    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    isAuthenticated,
    user,
    accessToken,
    authRoles,
    customerKey,
    legalKey,
    legalPassedFor,
    isLegalCheckComplete,
  ]);

  // Authorization check effect
  useEffect(() => {
    // Wait for Auth0 to finish checking authentication state before redirecting
    if (isLoading) {
      return;
    }

    // Redirect the user to the login page if they are not authenticated
    if (!isAuthenticated) {
      navigate('/', { replace: true, state: loginState.current });
      return;
    }

    if (isAuthenticated && user && accessToken && authMetadata) {
      // Check to see if the protected route is only for specific roles
      if (allowedRoles) {
        if (
          !authMetadata.roles ||
          !allowedRoles.some((role: string) => authMetadata?.roles?.includes(role))
        ) {
          navigate('/permission-denied', {
            replace: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the auth values only; `isLoading` is read as a guard, not a trigger
  }, [accessToken, authMetadata, isAuthenticated, user, allowedRoles, navigate]);

  // Track if organization fetch has been attempted to prevent duplicate requests
  const orgFetchAttemptedRef = useRef(false);

  // Fetch organization data if orgBID is available but dgraph_namespace is missing
  // This ensures dgraph_namespace is available for WebSocket connections
  useEffect(() => {
    const fetchOrganizationData = async () => {
      // Skip if already attempted, missing required data, or dgraph_namespace already exists
      if (
        orgFetchAttemptedRef.current ||
        !accessToken ||
        !authMetadata?.orgBID ||
        authMetadata.dgraph_namespace !== undefined
      ) {
        return;
      }

      // Mark as attempted to prevent duplicate requests
      orgFetchAttemptedRef.current = true;

      try {
        const organisation = await getOrganisationById(
          Config.middlewareApi,
          accessToken,
          authMetadata.orgBID,
        );
        if (organisation && organisation.dgraph_namespace !== undefined) {
          setAuthMetadata({
            dgraph_namespace: String(organisation.dgraph_namespace),
            dgraphUrl: organisation.dgraph_url
              ? organisation.dgraph_url.replace(/^https?:\/\//, '')
              : undefined,
            tsdbUrl: organisation.tsdb_url,
          });
        }
      } catch (error) {
        console.warn('Failed to fetch organization data for dgraph_namespace:', error);
      }
    };

    fetchOrganizationData();
  }, [accessToken, authMetadata?.orgBID, authMetadata?.dgraph_namespace, setAuthMetadata]);

  // Reset fetch attempt flag when orgBID changes (user switched organization)
  useEffect(() => {
    orgFetchAttemptedRef.current = false;
  }, [authMetadata?.orgBID]);

  // Create Apollo client with memoization to prevent recreation on renders
  const [apolloClient, setApolloClient] = useState<ExtendedApolloClient | null>(null);

  useEffect(() => {
    const createClient = async () => {
      if (!process.env.REACT_APP_DGRAPH_URL || !accessToken || !authMetadata?.orgBID) {
        setApolloClient(null);
        return;
      }

      try {
        // Let createGraphQLApolloClient handle its own singleton logic
        const endpoints = resolveEndpoints(
          process.env.REACT_APP_DGRAPH_URL,
          Config,
          Config.middlewareApi,
        );

        const client = await createGraphQLApolloClient(
          endpoints.httpUrl,
          endpoints.wsUrl,
          accessToken,
          authMetadata.orgBID,
          authMetadata.dgraph_namespace?.toString(),
        );
        setApolloClient(client);
      } catch (error) {
        console.error('Failed to create Apollo client:', error);
        setApolloClient(null);
      }
    };

    createClient();
  }, [accessToken, authMetadata?.orgBID, authMetadata?.dgraph_namespace]);

  // The client through Apollo's own context, always present: `ApolloProvider` refuses a missing
  // client, and rendering it only once the client exists remounted everything below it. With no
  // client, `useApolloClient` throws and `useSafeApolloClient` returns null, as before.
  const ApolloContext = getApolloContext();
  const apolloContextValue = useMemo(() => ({ client: apolloClient ?? undefined }), [apolloClient]);

  // Every gate has passed once. Before that, a gate shows the spinner page or redirects with no
  // providers mounted, so no live subscription or data read starts before the first terms answer.
  // After it, the spinner page takes the page's place inside the providers instead.
  const gatesPass =
    !isLoading &&
    isAuthenticated &&
    !(shouldRedirectToLegal && legalRedirectState) &&
    isLegalCheckComplete &&
    !!authMetadata;
  const [admitted, setAdmitted] = useState(false);
  useEffect(() => {
    if (gatesPass) setAdmitted(true);
  }, [gatesPass]);

  const spinner = <TLSpinnerPage loadingMessages={[]} />;
  let showSpinner = false;

  if (isLoading) {
    showSpinner = true;
  } else if (!isAuthenticated) {
    // Redirect immediately if not authenticated (no loading screen)
    // Use Navigate component for immediate redirect during render
    return <Navigate to="/" replace state={loginState.current} />;
  } else if (shouldRedirectToLegal && legalRedirectState) {
    // If Terms check determined we need to redirect to legal page, do it immediately
    return <Navigate to="/account/legal" replace state={legalRedirectState} />;
  } else if (!isLegalCheckComplete) {
    // Wait for Terms and Conditions check to complete before rendering
    // This prevents bypassing the Terms and Conditions screen by directly navigating to protected routes
    showSpinner = true;
  } else if (!authMetadata) {
    // Wait for AuthMetadataRestorer to restore authMetadata (roles, organisation) from the user object
    showSpinner = true;
  }

  // Before the gate first passes, the spinner renders alone: no provider mounts, so nothing reads
  // data before the terms answer. After that, it renders inside the providers, which stay mounted.
  if (showSpinner && !admitted) {
    return spinner;
  }

  return (
    <ApolloContext.Provider value={apolloContextValue}>
      <DataContextProvider
        middlewareApi={Config.middlewareApi}
        metricDataWarning={!!Config.metricDataWarning}
        resetKey={location.pathname}
      >
        <EventContextProvider resetKey={location.pathname}>
          <DialogContextProvider resetKey={location.pathname}>
            <DialogRenderer />
            {/* The onboarding handover's held picture (`spec-onboarding-persistent-scene-handover`,
                R1.1): here, so it outlives every route and step change. */}
            <PlanHandoverLayer />
            {/* The onboarding Modal (`spec-onboarding-persistent-scene-launch-into-live`, R2): here,
                so Launch's summary stays open across the route change into the live viewer. */}
            <OnboardingModalLayer userKey={user?.sub ?? null} />
            {showSpinner ? spinner : children}
          </DialogContextProvider>
        </EventContextProvider>
      </DataContextProvider>
    </ApolloContext.Provider>
  );
};

/**
 * The layout route above every protected page except the account pages. It finds the current
 * page's allowed roles in `routes` (the same table `App.tsx` builds the child routes from) and
 * renders the page through `<Outlet />`, so moving between its pages never unmounts it.
 */
const ProtectedLayout: React.FC<{ routes: ProtectedRouteEntry[] }> = ({ routes }) => {
  const { pathname } = useLocation();
  const route = routes.find(entry => matchPath({ path: entry.path, end: true }, pathname));

  if (!route) {
    // Unreachable while the child routes are built from `routes`: the router renders this layout
    // only for a matched child. A page added outside the table fails loudly in development, and
    // closed in production rather than open to every role.
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`ProtectedLayout: no entry in its route table for ${pathname}`);
    }
    return <Navigate to="/permission-denied" replace />;
  }

  return (
    <ProtectedGate allowedRoles={route.allowedRoles}>
      <Outlet />
    </ProtectedGate>
  );
};

export default ProtectedLayout;
